import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  useTheme
} from '@mui/material';
import {
  CloudUpload as SelectIcon,
  Edit as EditIcon,
  CheckCircle as ConfirmIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

interface ImportStepIndicatorProps {
  activeStep: number;
  completedSteps: number[];
}

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.divider,
    borderRadius: 1,
  },
  '&.Mui-active .MuiStepConnector-line': {
    backgroundImage: 'linear-gradient(90deg, #0F2A52 0%, #1976d2 100%)',
  },
  '&.Mui-completed .MuiStepConnector-line': {
    backgroundImage: 'linear-gradient(90deg, #0F2A52 0%, #1976d2 100%)',
  },
}));

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.grey[300],
  zIndex: 1,
  color: '#fff',
  width: 32,
  height: 32,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '1rem',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #0F2A52 0%, #1976d2 100%)',
    boxShadow: '0 2px 6px 0 rgba(0,0,0,.2)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #0F2A52 0%, #1976d2 100%)',
  }),
}));

function ColorlibStepIcon(props: any) {
  const { active, completed, className } = props;

  const icons: { [index: string]: React.ReactElement } = {
    1: <SelectIcon />,
    2: <EditIcon />,
    3: <ConfirmIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

const steps = [
  {
    label: 'Select File',
    description: 'Upload ZIP file to import'
  },
  {
    label: 'Edit Data',
    description: 'Review and edit comparison results'
  },
  {
    label: 'Confirm Import',
    description: 'Final confirmation and import'
  }
];

const ImportStepIndicator: React.FC<ImportStepIndicatorProps> = ({
  activeStep,
  completedSteps
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      width: '100%',
      mb: 2,
      p: 1.5,
      backgroundColor: 'rgba(15, 42, 82, 0.02)',
      borderRadius: 1,
      border: '1px solid rgba(15, 40, 76, 0.08)'
    }}>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          color: '#0F2A52',
          fontWeight: 600,
          mb: 1.5,
          textAlign: 'center',
          fontSize: '0.9rem'
        }}
      >
        Import Process
      </Typography>
      
      <Stepper 
        alternativeLabel 
        activeStep={activeStep} 
        connector={<ColorlibConnector />}
        sx={{
          '& .MuiStepLabel-root': {
            '& .MuiStepLabel-label': {
              color: '#0F2A52',
              fontWeight: 500,
              fontSize: '0.75rem',
              '&.Mui-active': {
                color: '#0F2A52',
                fontWeight: 600,
              },
              '&.Mui-completed': {
                color: '#0F2A52',
                fontWeight: 600,
              },
            },
            '& .MuiStepLabel-labelContainer': {
              '& .MuiStepLabel-iconContainer': {
                '& .MuiStepLabel-iconContainer-text': {
                  fill: 'transparent',
                },
              },
            },
          },
        }}
      >
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              StepIconComponent={ColorlibStepIcon}
              StepIconProps={{
                icon: index + 1,
                completed: completedSteps.includes(index),
                active: activeStep === index,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25, fontSize: '0.75rem' }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {step.description}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default ImportStepIndicator;
